import { useEffect, useState, useCallback } from 'react';
import { getCutApprovals, respondToApprovalRequest, cancelApprovalRequest } from '../../api/approvals';
import { CutApprovalsResponse, ApprovalRequest, User } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { Card } from '../ui/Card';
import { Loading } from '../ui/Loading';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Ban,
  MessageSquare,
  FileAudio,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

interface CutApprovalsProps {
  cutId: string;
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'PENDING':
      return <Clock className="w-4 h-4 text-yellow-500" />;
    case 'APPROVED':
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    case 'REJECTED':
      return <XCircle className="w-4 h-4 text-red-500" />;
    case 'CANCELLED':
      return <Ban className="w-4 h-4 text-gray-500" />;
    default:
      return <Clock className="w-4 h-4 text-muted" />;
  }
}

function getStatusBadgeClasses(status: string): string {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    case 'APPROVED':
      return 'bg-green-500/20 text-green-500 border-green-500/30';
    case 'REJECTED':
      return 'bg-red-500/20 text-red-500 border-red-500/30';
    case 'CANCELLED':
      return 'bg-gray-500/20 text-gray-500 border-gray-500/30';
    default:
      return 'bg-muted/20 text-muted border-muted/30';
  }
}

function UserAvatar({ user, size = 'sm' }: { user: Pick<User, 'id' | 'name' | 'avatarUrl'>; size?: 'sm' | 'md' }) {
  const sizeClasses = size === 'sm' ? 'w-5 h-5 text-[10px]' : 'w-7 h-7 text-xs';
  
  if (user.avatarUrl) {
    return (
      <img
        src={user.avatarUrl}
        alt={user.name}
        className={`${sizeClasses} rounded-full object-cover`}
        referrerPolicy="no-referrer"
      />
    );
  }
  
  // Generate color from user ID
  const colors = [
    'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
    'bg-purple-500', 'bg-pink-500', 'bg-indigo-500',
    'bg-red-500', 'bg-teal-500'
  ];
  let hash = 0;
  for (let i = 0; i < user.id.length; i++) {
    hash = user.id.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorClass = colors[Math.abs(hash) % colors.length];
  
  const initials = user.name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  
  return (
    <div className={`${sizeClasses} ${colorClass} rounded-full flex items-center justify-center text-white font-medium`}>
      {initials}
    </div>
  );
}

interface ApprovalItemProps {
  approval: ApprovalRequest;
  currentUserId: string | undefined;
  isAdmin: boolean;
  onRespond: (approvalId: string, status: 'APPROVED' | 'REJECTED') => void;
  onCancel: (approvalId: string) => void;
  isResponding: boolean;
}

function ApprovalItem({ approval, currentUserId, isAdmin, onRespond, onCancel, isResponding }: ApprovalItemProps) {
  // Can the current user respond to this approval?
  const canRespond = approval.status === 'PENDING' && 
    currentUserId && 
    currentUserId !== approval.requestedById &&
    (approval.targetUserId === currentUserId || !approval.targetUserId || isAdmin);
  
  // Can the current user cancel this approval?
  const canCancel = approval.status === 'PENDING' && 
    (currentUserId === approval.requestedById || isAdmin);

  return (
    <div className="relative pl-6 pb-6 last:pb-0">
      {/* Timeline connector line */}
      <div className="absolute left-[7px] top-6 bottom-0 w-0.5 bg-border last:hidden" />
      
      {/* Timeline dot */}
      <div className="absolute left-0 top-1 w-4 h-4 rounded-full bg-surface flex items-center justify-center border-2 border-border">
        <div className={`w-2 h-2 rounded-full ${
          approval.status === 'PENDING' ? 'bg-yellow-500' :
          approval.status === 'APPROVED' ? 'bg-green-500' :
          approval.status === 'REJECTED' ? 'bg-red-500' :
          'bg-gray-500'
        }`} />
      </div>

      {/* Content */}
      <div className="bg-surface-light rounded-lg p-3 border border-border hover:border-border/80 transition-colors">
        {/* Header row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <UserAvatar user={approval.requestedBy} />
            <span className="font-medium text-text text-sm">{approval.requestedBy.name}</span>
            <span className="text-muted text-sm">requested approval</span>
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getStatusBadgeClasses(approval.status)}`}>
              {approval.status}
            </span>
          </div>
          <span className="text-xs text-muted whitespace-nowrap">{formatTimeAgo(approval.createdAt)}</span>
        </div>

        {/* Comment content */}
        {approval.comment && (
          <div className="mt-2 flex items-start gap-2">
            <MessageSquare className="w-4 h-4 text-muted flex-shrink-0 mt-0.5" />
            <p className="text-sm text-muted line-clamp-2">{approval.comment.content}</p>
          </div>
        )}

        {/* Target info */}
        <div className="mt-2 text-xs text-muted">
          {approval.targetUser ? (
            <span className="flex items-center gap-1">
              Requested from <UserAvatar user={approval.targetUser} size="sm" />
              <span className="font-medium text-text">{approval.targetUser.name}</span>
            </span>
          ) : (
            <span>Open to any bandmate</span>
          )}
        </div>

        {/* Resolution info */}
        {approval.resolvedBy && approval.resolvedAt && (
          <div className="mt-2 pt-2 border-t border-border text-xs">
            <span className="flex items-center gap-1 text-muted">
              {getStatusIcon(approval.status)}
              <span className={approval.status === 'APPROVED' ? 'text-green-500' : approval.status === 'REJECTED' ? 'text-red-500' : 'text-muted'}>
                {approval.status === 'APPROVED' ? 'Approved' : approval.status === 'REJECTED' ? 'Rejected' : 'Cancelled'}
              </span>
              by
              <UserAvatar user={approval.resolvedBy} size="sm" />
              <span className="font-medium text-text">{approval.resolvedBy.name}</span>
              <span className="text-muted">• {formatTimeAgo(approval.resolvedAt)}</span>
            </span>
            {approval.responseNote && (
              <p className="mt-1 text-muted italic">"{approval.responseNote}"</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        {(canRespond || canCancel) && (
          <div className="mt-3 pt-2 border-t border-border flex items-center gap-2">
            {canRespond && (
              <>
                <button
                  onClick={() => onRespond(approval.id, 'APPROVED')}
                  disabled={isResponding}
                  className="px-3 py-1.5 text-xs bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3 h-3" />
                  Approve
                </button>
                <button
                  onClick={() => onRespond(approval.id, 'REJECTED')}
                  disabled={isResponding}
                  className="px-3 py-1.5 text-xs border border-red-500 text-red-500 rounded-md hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                >
                  <XCircle className="w-3 h-3" />
                  Reject
                </button>
              </>
            )}
            {canCancel && (
              <button
                onClick={() => onCancel(approval.id)}
                disabled={isResponding}
                className="px-3 py-1.5 text-xs text-muted hover:text-error transition-colors ml-auto"
              >
                Cancel Request
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function CutApprovals({ cutId }: CutApprovalsProps) {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState<CutApprovalsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [isResponding, setIsResponding] = useState(false);

  const fetchApprovals = useCallback(async () => {
    try {
      const response = await getCutApprovals(cutId);
      setData(response);
      // Auto-expand files that have pending approvals
      const filesWithPending = new Set(
        response.approvalsByFile
          .filter(f => f.approvals.some(a => a.status === 'PENDING'))
          .map(f => f.file.id)
      );
      setExpandedFiles(filesWithPending);
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
      setError('Failed to load approvals');
    } finally {
      setIsLoading(false);
    }
  }, [cutId]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const toggleFileExpanded = (fileId: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const handleRespond = async (approvalId: string, status: 'APPROVED' | 'REJECTED') => {
    setIsResponding(true);
    try {
      await respondToApprovalRequest(approvalId, { status });
      await fetchApprovals();
    } catch (err) {
      console.error('Failed to respond to approval:', err);
      alert('Failed to respond to approval request. Please try again.');
    } finally {
      setIsResponding(false);
    }
  };

  const handleCancel = async (approvalId: string) => {
    setIsResponding(true);
    try {
      await cancelApprovalRequest(approvalId);
      await fetchApprovals();
    } catch (err) {
      console.error('Failed to cancel approval:', err);
      alert('Failed to cancel approval request. Please try again.');
    } finally {
      setIsResponding(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="text-center py-12">
        <Loading />
        <p className="text-muted mt-4">Loading approvals...</p>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="text-center py-12">
        <XCircle className="w-12 h-12 text-error mx-auto mb-4 opacity-50" />
        <p className="text-error">{error}</p>
      </Card>
    );
  }

  if (!data || data.summary.total === 0) {
    return (
      <Card className="text-center py-12">
        <CheckCircle2 className="w-12 h-12 text-muted mx-auto mb-4 opacity-50" />
        <p className="text-muted">No approval requests yet</p>
        <p className="text-sm text-muted mt-1">
          Request approval on comments to get feedback from your bandmates
        </p>
      </Card>
    );
  }

  const { summary, approvalsByFile } = data;

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <Card className="!p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-text">{summary.total} approval request{summary.total !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-3 text-sm">
              {summary.pending > 0 && (
                <span className="flex items-center gap-1 text-yellow-500">
                  <Clock className="w-4 h-4" />
                  {summary.pending} pending
                </span>
              )}
              {summary.approved > 0 && (
                <span className="flex items-center gap-1 text-green-500">
                  <CheckCircle2 className="w-4 h-4" />
                  {summary.approved} approved
                </span>
              )}
              {summary.rejected > 0 && (
                <span className="flex items-center gap-1 text-red-500">
                  <XCircle className="w-4 h-4" />
                  {summary.rejected} rejected
                </span>
              )}
              {summary.cancelled > 0 && (
                <span className="flex items-center gap-1 text-gray-500">
                  <Ban className="w-4 h-4" />
                  {summary.cancelled} cancelled
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Approvals by File */}
      <div className="space-y-3">
        {approvalsByFile.filter(f => f.approvals.length > 0).map(({ file, approvals }) => {
          const isExpanded = expandedFiles.has(file.id);
          const pendingCount = approvals.filter(a => a.status === 'PENDING').length;
          const approvedCount = approvals.filter(a => a.status === 'APPROVED').length;
          const rejectedCount = approvals.filter(a => a.status === 'REJECTED').length;

          return (
            <Card key={file.id} className="!p-0 overflow-hidden">
              {/* File Header */}
              <button
                onClick={() => toggleFileExpanded(file.id)}
                className="w-full flex items-center justify-between p-3 hover:bg-surface-light transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted" />
                  )}
                  <FileAudio className="w-5 h-5 text-primary" />
                  <div>
                    <span className="font-medium text-text">{file.name || file.originalName}</span>
                    <span className="text-xs text-muted ml-2">
                      uploaded by {file.uploadedBy.name}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {pendingCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-500/20 text-yellow-500">
                      {pendingCount} pending
                    </span>
                  )}
                  {approvedCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-500">
                      {approvedCount} approved
                    </span>
                  )}
                  {rejectedCount > 0 && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-500">
                      {rejectedCount} rejected
                    </span>
                  )}
                </div>
              </button>

              {/* Timeline */}
              {isExpanded && (
                <div className="border-t border-border p-4">
                  {approvals.map((approval) => (
                    <ApprovalItem
                      key={approval.id}
                      approval={approval}
                      currentUserId={user?.id}
                      isAdmin={isAdmin}
                      onRespond={handleRespond}
                      onCancel={handleCancel}
                      isResponding={isResponding}
                    />
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
